import { Typography } from '@material-ui/core';
import { useEffect, useRef } from 'react';
import type {
  CockpitArtifactView,
  CockpitSkillView,
  CockpitViewNode,
} from '../model/cockpitViewModel';
import {
  coverageLabel,
  lifecycleLabel,
  provenanceLabel,
} from '../model/semanticLabels';
import { useCockpitStyles } from './cockpitStyles';
import { EvidenceConfidenceMarker } from './EvidenceConfidenceMarker';
import { commonNonEmptyValue } from './evidenceRecordPresentation';

export interface EvidenceLaneProps {
  title: string;
  nodes: CockpitViewNode[];
  selectedId: string | null;
  selectedKind: CockpitViewNode['kind'] | null;
  relatedIds: string[];
  onSelect: (kind: CockpitViewNode['kind'], id: string) => void;
}

const EvidenceNodeButton = ({
  selected,
  className,
  onClick,
  children,
  ...rest
}: React.ComponentProps<'button'> & { selected: boolean }) => {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!selected) return;
    const node = ref.current;
    if (!node || node.contains(document.activeElement)) return;
    node.focus();
    node.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={className}
      aria-expanded={selected}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const EvidenceLane = ({
  title,
  nodes,
  selectedId,
  selectedKind,
  relatedIds,
  onSelect,
}: EvidenceLaneProps) => {
  const classes = useCockpitStyles();
  const commonSource = commonNonEmptyValue(
    nodes.filter(hasSource).map(node => nodeSourceLabel(node)),
  );
  return (
    <section className={classes.lane} aria-label={title}>
      <Typography component="h3" className={classes.laneTitle}>
        {title}
      </Typography>
      {nodes.map(node => {
        const selected = node.id === selectedId && node.kind === selectedKind;
        const dimmed = Boolean(
          selectedId && !selected && !relatedIds.includes(node.id),
        );
        return (
          <EvidenceNodeButton
            key={node.id}
            selected={selected}
            className={`${classes.node} ${selected ? classes.selected : ''} ${
              dimmed ? classes.dimmed : ''
            }`}
            onClick={() => onSelect(node.kind, node.id)}
          >
            <EvidenceNodeContent
              node={node}
              selected={selected}
              showSource={!commonSource}
            />
          </EvidenceNodeButton>
        );
      })}
      {commonSource && <CommonSourceFooter source={commonSource} />}
    </section>
  );
};

function EvidenceNodeContent({
  node,
  selected,
  showSource,
}: {
  node: CockpitViewNode;
  selected: boolean;
  showSource: boolean;
}) {
  const classes = useCockpitStyles();
  return (
    <>
      <Typography
        component="span"
        variant="body2"
        className={classes.nodeLabel}
      >
        {node.label}
      </Typography>
      <Typography component="span" variant="body2" className={classes.nodeMeta}>
        <EvidenceConfidenceMarker confidence={node.confidence} />
        <span aria-hidden="true">·</span>
        <span>{coverageLabel(node.coverage)}</span>
      </Typography>
      <EvidenceNodeVersion node={node} />
      {showSource && hasSource(node) && <EvidenceNodeSource node={node} />}
      <EvidenceNodeSelection node={node} selected={selected} />
    </>
  );
}

function EvidenceNodeVersion({ node }: { node: CockpitViewNode }) {
  const classes = useCockpitStyles();
  const version = nodeVersion(node);
  if (version === undefined) return null;
  return (
    <Typography component="span" variant="body2" className={classes.nodeLine}>
      Versão{' '}
      <code className={classes.technical}>{version ?? 'não informada'}</code>
    </Typography>
  );
}

function EvidenceNodeSource({
  node,
}: {
  node: CockpitSkillView | CockpitArtifactView;
}) {
  const classes = useCockpitStyles();
  return (
    <Typography component="span" variant="body2" className={classes.nodeLine}>
      Origem:{' '}
      <code className={classes.technical}>
        {nodeSourceLabel(node) || 'não informada'}
      </code>
    </Typography>
  );
}

function EvidenceNodeSelection({
  node,
  selected,
}: {
  node: CockpitViewNode;
  selected: boolean;
}) {
  const classes = useCockpitStyles();
  return (
    <Typography
      component="span"
      variant="body2"
      className={`${classes.nodeLine} ${classes.nodeSelectionReserved} ${
        selected ? classes.nodeSelectionVisible : ''
      }`}
      aria-hidden={!selected}
    >
      ID: <code className={classes.technical}>{node.id}</code> · Ciclo de vida:{' '}
      {lifecycleLabel(node.lifecycle)}
    </Typography>
  );
}

function CommonSourceFooter({ source }: { source: string }) {
  const classes = useCockpitStyles();
  return (
    <Typography variant="caption" className={classes.laneFooter}>
      Origem comum: <code className={classes.technical}>{source}</code>
    </Typography>
  );
}

function hasSource(
  node: CockpitViewNode,
): node is CockpitSkillView | CockpitArtifactView {
  return node.kind === 'skill' || node.kind === 'artifact';
}

function nodeSourceLabel(node: CockpitSkillView | CockpitArtifactView): string {
  return node.kind === 'skill'
    ? node.sourceLabel
    : provenanceLabel(node.provenance);
}

function nodeVersion(node: CockpitViewNode): string | null | undefined {
  return hasSource(node) ? node.version : undefined;
}
